import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, Loader2, Truck } from 'lucide-react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ApiCallError, getCheckInCandidates, type KioskLookupMatch } from '@/api/client';
import type { CheckInDto } from '@/api/types';

/**
 * WHICH LOAD IS THIS DRIVER HERE FOR? — THE SCHEDULER DECIDES, AFTER THE LICENCE.
 *
 * ---- ⭐ WHY THIS EXISTS AND WHY IT OPENS WHEN IT DOES ----------------------
 *
 * Pete, 2026-08-25: *"I don't think we are giving the driver the ability to assign
 * themselves to an appointment. They are not going to look at the paperwork and confirm
 * the info consistently and accurately. Think this process needs to goto the scheduler
 * screen right after he scans the drivers license."* And: *"Scheduler should be
 * associating the driver to an appointment after the drivers license scan. Or confirming
 * it, if it finds only one appointment."*
 *
 * ⭐ HIS ORDERING IS BETTER ON THE EVIDENCE, NOT JUST THE WORKFLOW. At the gate all we
 * had was a typed phone number — which migration 144 says in its own comments is NOT
 * unique (a shared cab phone is real) and can be mistyped. After the scan we have the
 * licence number, which that migration calls "the reliable key". So matching here is
 * matching on better information than matching at the tablet ever could.
 *
 * ---- ⛔ WHAT THIS SCREEN REFUSES TO DO -------------------------------------
 *
 * ⛔ NOTHING IS PRE-SELECTED WHEN THERE IS MORE THAN ONE. A tired scheduler accepting a
 * pre-ticked wrong answer at 3am is the entire risk of automating this, and a driver can
 * legitimately have several loads in one day — Pete raised exactly that.
 *
 * ⛔ AND IT SHOWS THE EVIDENCE, NEVER A CONFIDENCE SCORE. "Matched on container" can be
 * checked in two seconds against the paper in the scheduler's hand; "92% confident"
 * cannot be checked at all, so it is either trusted blindly or ignored. Same rule the
 * OCR design was written under.
 *
 * ⚠️ IT IS NOT A PRECONDITION. The scan is the natural trigger — it is when the
 * scheduler is already looking at this driver — but a licence that will not read, or one
 * left in the cab, must not mean the truck cannot be attached. So this is reachable from
 * the card at any time, and it says so when it finds nothing.
 */

interface Props {
    open: boolean;
    checkIn: CheckInDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    /** Attach this arrival to that load. The caller owns the substitute-driver question. */
    onAttach: (c: CheckInDto, appointmentId: string) => void;
    /** No load fits — create one from what the arrival already told us. */
    onCreate: (c: CheckInDto) => void;
}

/** ⚠️ The server's own words for what matched, in the scheduler's. */
const SIGNAL: Record<string, string> = {
    phone: 'his phone number',
    container: 'the container number',
    reference: 'the reference number',
    po: 'the PO number',
    driver: 'the driver',
    manual: 'a manual link',
};

export function MatchArrivalDialog({ open, checkIn, getToken, onClose, onAttach, onCreate }: Props) {
    const [matches, setMatches] = useState<KioskLookupMatch[] | null>(null);
    const [method, setMethod] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!checkIn) return;
        setLoading(true);
        setError(null);
        try {
            const r = await getCheckInCandidates(await getToken(), checkIn.checkInId);
            setMatches(r.matches);
            setMethod(r.method);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not look for matching appointments.');
            setMatches([]);
        } finally {
            setLoading(false);
        }
    }, [checkIn, getToken]);

    useEffect(() => {
        if (!open) { setMatches(null); setMethod(null); setError(null); return; }
        void load();
    }, [open, load]);

    if (!checkIn) return null;

    const one = matches?.length === 1;

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        Which load is this? · arrival #{checkIn.arrivalSeq ?? '—'}
                    </DialogTitle>
                    <DialogDescription>
                        {checkIn.driverName || 'Driver not named'}
                        {checkIn.driverPhoneEntered ? ` · ${checkIn.driverPhoneEntered}` : ''}
                        {/**
                          * ⭐ THE SIGNAL IS NAMED, so a scheduler can check it against the
                          * paperwork rather than take the screen's word for it.
                          */}
                        {method && SIGNAL[method] ? ` — found on ${SIGNAL[method]}` : ''}
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                        <span>{error}</span>
                    </div>
                )}

                {loading && matches === null ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Looking…
                    </div>
                ) : (matches?.length ?? 0) === 0 ? (
                    /**
                     * ⛔ "NOTHING MATCHED" IS SAID OUT LOUD AND IS NOT AN ERROR. A walk-up
                     * with no booking is a normal morning. Silence here would read as a
                     * broken search and send somebody looking for a fault.
                     */
                    <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                        Nothing on today's schedule matches this driver.
                        <div className="mt-1 text-xs">
                            Create an appointment for him, or attach one by hand from the card.
                        </div>
                    </div>
                ) : (
                    <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
                        {/**
                          * ⭐ ONE CANDIDATE IS A CONFIRMATION, SEVERAL IS A CHOICE — Pete:
                          * *"Or confirming it, if it finds only one appointment."* The
                          * wording changes so a scheduler can tell which situation they are
                          * in without counting the rows.
                          */}
                        <div className="text-xs text-muted-foreground">
                            {one
                                ? 'One load matches. Check it against his paperwork, then confirm.'
                                : `${matches?.length} appointments match this driver today — pick the right one.`}
                        </div>
                        {(matches ?? []).map((m) => (
                            <div
                                key={m.appointmentId}
                                className="flex items-start gap-2 rounded border p-2"
                            >
                                <Truck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                                <div className="min-w-0 flex-1 text-xs">
                                    <div className="font-medium">
                                        {m.appointmentNumber}
                                        {m.direction ? ` · ${m.direction}` : ''}
                                    </div>
                                    <div className="text-muted-foreground">
                                        {m.scheduledLocal}
                                        {m.doorCode ? ` · door ${m.doorCode}` : ' · no door yet'}
                                        {m.clientCode ? ` · ${m.clientCode}` : ''}
                                        {m.carrierCode ? ` · ${m.carrierCode}` : ''}
                                    </div>
                                    {/**
                                      * ⚠️ THE NUMBERS OFF THE PAPERWORK, because that is what
                                      * the scheduler is holding. A row a person cannot check
                                      * against the document in their hand is a row they can
                                      * only accept on faith.
                                      */}
                                    {(m.poNumber || m.containerNumber || m.referenceNumber) && (
                                        <div className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                                            {[m.poNumber && `PO ${m.poNumber}`,
                                              m.containerNumber && `container ${m.containerNumber}`,
                                              m.referenceNumber && `ref ${m.referenceNumber}`]
                                                .filter(Boolean).join(' · ')}
                                        </div>
                                    )}
                                    {m.driverName && (
                                        <div className="mt-0.5 text-[10px] text-muted-foreground">
                                            booked for {m.driverName}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    variant={one ? 'default' : 'outline'}
                                    onClick={() => { onAttach(checkIn, String(m.appointmentId)); onClose(); }}
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    {one ? 'Confirm' : 'This one'}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full items-center justify-between gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { onCreate(checkIn); onClose(); }}
                        >
                            New appointment
                        </Button>
                        {/**
                          * ⭐ "NOT NOW" RATHER THAN "CANCEL". Closing this leaves the arrival
                          * unattached, which is a perfectly good state — the truck is on site
                          * and somebody will deal with it. "Cancel" would suggest something
                          * was undone.
                          */}
                        <Button size="sm" onClick={onClose}>Not now</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
